import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import {
  aggregateFanOutQueryState,
  mergeDailyTrends,
} from '~/components/PriceParrot/PriceParrotChainLpcLppRatioDashboard/utils';

import PriceParrotService from '~/services/PriceParrotService';

import type { ChainLpcLppSupplierDailyTrendPoint } from '~/types/services/priceParrot';

import { chainLpcLppSupplierQueryKeys } from './chainLpcLppSupplierQueryKeys';

/** Chain-wide daily trend — omitting offer_bk rolls it up across the whole chain instead of one property. */
export function useGetChainLpcLppRollupTrend(
  chainNames: ReadonlyArray<string>,
  dateFrom: string | undefined,
  dateTo: string | undefined,
  enabled: boolean,
) {
  const queries = useQueries({
    queries: chainNames.map((chainName) => ({
      queryKey: chainLpcLppSupplierQueryKeys.rollupTrend(chainName, dateFrom ?? '', dateTo ?? ''),
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        if (!dateFrom || !dateTo) {
          throw new Error('Chain LPC-LPP rollup trend requires dateFrom and dateTo');
        }
        const res = await PriceParrotService.getParityTrendChainLpcLppDailyTrend(
          chainName,
          { date_from: dateFrom, date_to: dateTo },
          signal,
        );
        return res.data;
      },
      enabled: enabled && Boolean(chainName && dateFrom && dateTo),
    })),
  });

  const { isFetching, isError, error } = aggregateFanOutQueryState(chainNames, queries);

  const daily = useMemo<Array<ChainLpcLppSupplierDailyTrendPoint>>(
    () => mergeDailyTrends(queries.map((q) => q.data?.daily ?? [])),
    [queries],
  );

  return { daily, isFetching, isError, error };
}
